import "./FMOverview.scss";

import Documents from "components/Documents/drawer/DocumentsDrawer";
import ErrorState from "components/Error/ErrorState";
import LoadingIcon from "components/Loading/LoadingIcon";
import OverviewStatusHeader from "components/StatusHeader/OverviewStatusHeader";
import { getStatusTitles } from "components/StatusHeader/StatusHeaderUtils";
import { SECTION } from "constants/SettingsConstants";
import { compareDesc, parseISO } from "date-fns";
import ClientInformationDrawer from "pages/client/ClientInformationDrawer/ClientInformationDrawer";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { setIsLoading, setValidationState } from "reducers/main";
import { useGetAmendedReviewsByFileIdQuery } from "services/ar-screening-api";
import { useGetCostProjectionsByFileIdQuery } from "services/cost-projection-api";
import { useGetOrganizationSettingsQuery } from "services/misc-api";
import { useGetMSAByFileIdQuery } from "services/msa-api";
import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  MSAWithValidationDataType,
} from "types/DataTypes";
import { SERVICE_TYPE } from "types/DataTypes";
import { useDispatch } from "utils/redux";

const sortByCreatedDesc = <T extends { created: string }>(items: T[]) =>
  [...items].sort((a, b) => {
    // convert dates to ISO format for comparison
    const dateA = new Date(a.created).toISOString();
    const dateB = new Date(b.created).toISOString();
    return compareDesc(parseISO(dateA), parseISO(dateB));
  });

type FutureMedicalOverviewProps = {
  className?: string;
};

const FutureMedicalOverview = ({ className }: FutureMedicalOverviewProps) => {
  const dispatch = useDispatch();
  const { data: organizationSettings } = useGetOrganizationSettingsQuery();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const fileId = params.get("file_id");

  const [topService, setTopService] =
    useState<MSAWithValidationDataType | null>(null);

  // fetch MSAs and amended reviews
  const {
    data: msaList,
    isLoading: msaLoading,
    error: msaError,
  } = useGetMSAByFileIdQuery({ fileId }, { skip: !fileId });

  // fetch cost projections
  const {
    data: costProjections,
    isLoading: cpLoading,
    error: cpError,
  } = useGetCostProjectionsByFileIdQuery({ fileId }, { skip: !fileId });

  // amended review screenings
  const {
    data: amendedReviewScreenings,
    isLoading: arScreensLoading,
    error: arScreensError,
  } = useGetAmendedReviewsByFileIdQuery({ fileId }, { skip: !fileId });

  // clear validation state on mount
  useEffect(() => {
    dispatch(setValidationState({ errors: [], warnings: [] }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setIsLoading(msaLoading || cpLoading || arScreensLoading));
  }, [dispatch, msaLoading, cpLoading, arScreensLoading]);

  // memoize sorted MSAs and projections
  const { currentDocService, olderDocuments } = useMemo(() => {
    if (
      !(msaList as unknown as { results: MSAWithValidationDataType[] })
        ?.results ||
      !(costProjections as unknown as { results: CostProjectionDataType[] })
        ?.results ||
      !(
        amendedReviewScreenings as unknown as {
          results: AmendedReviewScreeningType[];
        }
      )?.results
    )
      return {};

    // combine all documents and sort by created date, newest first
    const allDocuments = sortByCreatedDesc([
      ...(msaList as unknown as { results: MSAWithValidationDataType[] })
        .results,
      ...(costProjections as unknown as { results: CostProjectionDataType[] })
        .results,
      ...(
        amendedReviewScreenings as unknown as {
          results: AmendedReviewScreeningType[];
        }
      ).results,
    ]);

    // first document is "Current"
    const [current, ...older] = allDocuments;

    // update local top service state instead of dispatching to Redux
    if (current && current !== topService) {
      setTopService(current as unknown as MSAWithValidationDataType);
    }

    return {
      currentDocService: current,
      olderDocuments: older,
    };
  }, [
    (msaList as unknown as { results: MSAWithValidationDataType[] })?.results,
    (costProjections as unknown as { results: CostProjectionDataType[] })
      ?.results,
    (
      amendedReviewScreenings as unknown as {
        results: AmendedReviewScreeningType[];
      }
    )?.results,
    topService,
  ]);

  // update the document type mapping
  const getDocumentTypeLabel = (doc: MSAWithValidationDataType) => {
    if (doc.type === SERVICE_TYPE.MSA) return SECTION.MSA;
    if (doc.type === SERVICE_TYPE.NTMSA) return SECTION.NTMSA;
    if (doc.type === SERVICE_TYPE.AMENDED_REVIEW) return SECTION.AMENDED_REVIEW;
    if (doc.type === SERVICE_TYPE.LCP) return SECTION.LCP;
    if (doc.type === SERVICE_TYPE.MCP) return SECTION.MCP;
    if (doc.type === SERVICE_TYPE.MSA_111) return SECTION.MSA_111;
    if (doc.type === SERVICE_TYPE.PRE_MSA) return SECTION.PRE_MSA;

    // since no type is defined for amended review screenings, check if ID matches
    const isAmendedReviewScreening = (doc: MSAWithValidationDataType) =>
      (
        amendedReviewScreenings as unknown as {
          results: AmendedReviewScreeningType[];
        }
      )?.results?.some((ar: AmendedReviewScreeningType) => ar.id === doc.id);
    if (isAmendedReviewScreening(doc)) {
      return SECTION.AMENDED_REVIEW_SCREENING;
    }
    // fallback to MSA if type is not defined
    return SECTION.MSA;
  };

  if (msaLoading || cpLoading || arScreensLoading) {
    return <LoadingIcon />;
  }

  if (msaError || cpError || arScreensError) {
    return <ErrorState />;
  }

  return (
    <div className={`overview-page ${className || ""}`}>
      <div className="timeline-overview">
        <div className="grid-layout status-overview">
          <div className="future-medical-group">
            {/* current service status */}
            {currentDocService && (
              <OverviewStatusHeader
                title={`Current ${getDocumentTypeLabel(currentDocService as MSAWithValidationDataType)}`}
                workspaceUrl={
                  (currentDocService as MSAWithValidationDataType).workspace_url
                }
                items={Object.entries(
                  getStatusTitles(
                    {
                      type: getDocumentTypeLabel(
                        currentDocService as MSAWithValidationDataType,
                      ),
                      validation: (
                        currentDocService as MSAWithValidationDataType
                      ).validation,
                      id: (
                        currentDocService as MSAWithValidationDataType
                      ).id.toString(),
                    },
                    organizationSettings?.quickbooks_integration_enabled ??
                      true,
                  ),
                ).map(([title, { status, url }]) => ({
                  title,
                  status,
                  url,
                }))}
              />
            )}
          </div>

          {/* all other documents */}
          <div className="future-medical-group older-documents">
            {olderDocuments?.map((doc: MSAWithValidationDataType) => {
              const docType = getDocumentTypeLabel(doc);

              return (
                <OverviewStatusHeader
                  key={doc.id}
                  title={`${docType} (${new Date(doc.created).toLocaleDateString()})`}
                  workspaceUrl={doc.workspace_url}
                  items={Object.entries(
                    getStatusTitles(
                      {
                        type: docType,
                        validation: doc.validation,
                        id: doc.id.toString(),
                      },
                      organizationSettings?.quickbooks_integration_enabled ??
                        true,
                    ),
                  ).map(([title, { status, url }]) => ({
                    title,
                    status,
                    url,
                  }))}
                />
              );
            })}
          </div>
        </div>
      </div>
      <ClientInformationDrawer service={currentDocService} />
      <Documents fileId={Number(fileId)} />
    </div>
  );
};

export default FutureMedicalOverview;
